import './Settings.sass';

import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import router from 'navigation/router';

import { Button, Header, List, ListItem, ScrollView } from '$uikit';
import {
  IcChevron16,
  IcBlockedCountries28,
  IcBot28,
  IcKey28,
  IcNotifications28,
  IcSignOut28,
  IcSettings28,
  IcBubble24,
  IcArrowCounterClockWise28,
  IcAngleRight28,
} from '$assets';
import { authActions, authSelector } from '$store/auth';
import { useCurrentLang, useCurrentUserInfo, useTranslator } from '$hooks';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import {
  BIND_EMAIL,
  BLACKLIST,
  CONTENT_PRIORITY,
  LANGUAGE,
  NOTIFY_SETTINGS,
  PASSKEY,
  PASSWORD,
  SESSIONS,
  TELEGRAM,
  WEBSITE_SETTINGS,
  WELCOME_MESSAGE,
} from '$shared/constants/pages';
import { mainActions, mainSelector } from '$store/main';
import { toastActions } from '$store/toast';
import { _confirm, getDocumentHref } from '$utils';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';

import { LanguageTags } from '$shared/constants';
import { ContentPriorityType } from '$store/main/interface';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';

export const Settings: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const user = useCurrentUserInfo();
  const { isLogged } = useSelector(authSelector);
  const {
    mVerified,
    hasPassword,
    contentPriority,
    canWithdraw,
    shouldBindEmail,
    hasBot,
  } = useSelector(mainSelector);
  const currentLang = useCurrentLang();
  const contentPriorityIndicator =
    contentPriority === ContentPriorityType.ADULT
      ? t('edit_content_priority_button_adult')
      : t('edit_content_priority_button_other');
  const email = 'legal@ton.place';

  const handlePasskey = useCallback(() => {
    if (window.isWebApp || !browserSupportsWebAuthn()) {
      dispatch(toastActions.setToastFail(t('passkey_not_supported')));
      return;
    }
    router.navigate(PASSKEY);
  }, [dispatch, t]);

  const handleLogout = () => {
    _confirm(t('settings_logout_confirm'), () => dispatch(authActions.logout()));
  };

  const handleReload = () => {
    // @ts-ignore
    location.reload(true);
  };

  const handleLegal = useCallback(() => {
    dispatch(
      mainActions.setPopup(
        <BottomSheet title={t('settings_legal')}>
          <div className="Settings__BottomSheet__text">
            {t('copyright_ownership_notification', { email })}
          </div>
          <Button
            onClick={() => location.assign(`mailto: ${email}`)}
            className="Settings__BottomSheet__button"
            iconRight={<IcAngleRight28 />}
          >
            {t('copyright_ownership_button')}
          </Button>
        </BottomSheet>,
      ),
    );
  }, []);

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <Header showBackButton>{t('settings_title')}</Header>
      <ScrollView>
        <div className="Settings">
          <List>
            <ListItem modal="domain" indicator={user.domain || `id${user.id}`}>
              {t('settings_username')}
            </ListItem>
          </List>
          <List>
            <ListItem routeName={LANGUAGE} indicator={currentLang.label}>
              {t('settings_language')}
            </ListItem>
            <ListItem routeName={NOTIFY_SETTINGS} icon={<IcNotifications28 />}>
              {t('settings_notify')}
            </ListItem>
            {mVerified && (
              <ListItem routeName={WELCOME_MESSAGE} icon={<IcBubble24 />}>
                {t('welcome_message_title')}
              </ListItem>
            )}
            {(hasBot || mVerified) && (
              <ListItem routeName={TELEGRAM} icon={<IcBot28 />}>
                {t('other_telegram_bot')}
              </ListItem>
            )}
            <ListItem routeName={WEBSITE_SETTINGS} icon={<IcSettings28 />}>
              {t('settings_website_settings')}
            </ListItem>
            <ListItem modal="edit_countries" icon={<IcBlockedCountries28 />}>
              {t('settings_countries')}
            </ListItem>
            {shouldBindEmail && canWithdraw && (
              <ListItem routeName={BIND_EMAIL}>{t('bind_email_settings_item')}</ListItem>
            )}
            <ListItem routeName={SESSIONS}>{t('settings_sessions')}</ListItem>
            <ListItem routeName={BLACKLIST}>{t('blacklist_title')}</ListItem>
            {mVerified && (
              <ListItem
                routeName={PASSWORD}
                indicator={
                  hasPassword ? t('settings_auth_with_pass_has_pass') : undefined
                }
              >
                {t('settings_auth_with_pass')}
              </ListItem>
            )}
            {canWithdraw && browserSupportsWebAuthn() && (
              <ListItem onClick={handlePasskey} icon={<IcKey28 />}>
                Passkey
              </ListItem>
            )}
            {!window.isMedia &&
              currentLang.key !== LanguageTags.id &&
              contentPriority !== ContentPriorityType.ADULT && (
                <ListItem
                  routeName={CONTENT_PRIORITY}
                  indicator={contentPriorityIndicator}
                >
                  {t('settings_content_priority')}
                </ListItem>
              )}
          </List>
          <List>
            <ListItem
              href={getDocumentHref('terms')}
              icon={<IcChevron16 />}
              iconSize="small"
            >
              {t('settings_terms')}
            </ListItem>
            <ListItem href="/privacy.html" icon={<IcChevron16 />} iconSize="small">
              {t('settings_privacy')}
            </ListItem>
            <ListItem onClick={handleLegal} icon={<IcChevron16 />} iconSize="small">
              {t('settings_legal')}
            </ListItem>
          </List>
          <List>
            <ListItem icon={<IcArrowCounterClockWise28 />} onClick={handleReload}>
              {t('settings_reload')}
            </ListItem>
          </List>
          {!window.isWebApp && (
            <List>
              <ListItem icon={<IcSignOut28 />} onClick={handleLogout}>
                {t('settings_logout')}
              </ListItem>
            </List>
          )}
        </div>
      </ScrollView>
    </>
  );
};
